import { useMemo } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { api } from '../services'
import { queryKeys } from '../lib/queryClient'
import { DEFAULT_LIMIT, LocalStorageItem } from '../common/constants'
import { readingsCacheService } from '../services/cache/readingsCacheService'
import { cacheSettingsService } from '../services/cache/cacheSettingsService'
import { useAuthContext } from '../contexts/AuthContext'
import { splitIntoProgressiveWindows } from '../utils/rangeWindowing'

/**
 * Sensor reading data point
 */
export interface Reading {
  timestamp: number
  [key: string]: any
}

/**
 * Sensor data from API
 */
export interface SensorData {
  productName: string
  sensorName: string
  units: string
  data: Reading[]
  hidden?: { [timestamp: string]: boolean }  // Hidden timestamps for client-side filtering
  [key: string]: any
}

/**
 * API response structure for getReadings (nested format)
 */
export interface GetReadingsResponse {
  [sensorKey: string]: SensorData  // e.g., "A1-13", "B2-12"
}

/**
 * Transformed response for compatibility
 */
export interface TransformedReadingsResponse {
  readings: Reading[]  // Flat array for backward compatibility
  sensors: GetReadingsResponse  // Nested sensor data
  sigfoxId: string
  from: number
  to: number
  limit: number
  cacheSource?: 'cache' | 'api' | 'mixed'
  cacheHitRatio?: number
  latencyMs?: number  // Total fetch time in milliseconds
}

/**
 * Fetch one window of readings: IndexedDB cache first, then the API (always, so
 * sensors added since the cache was written are still discovered), merged.
 */
async function fetchReadingsRange(
  sigfoxId: string,
  from: number,
  to: number,
  limit: number,
  contextEmail: string
): Promise<TransformedReadingsResponse> {
  const startTime = performance.now()

  // Get email inside queryFn to ensure we have the latest value
  const localStorageEmail = localStorage.getItem(LocalStorageItem.email)
  const email = contextEmail || localStorageEmail || ''


  // If no email, fall back to direct API call (shouldn't happen in normal flow)
  if (!email) {
    console.warn('No user email available, skipping cache')
    const apiResult = await api.getReadings(sigfoxId, from, to, limit)
    const sensors = apiResult.data || {}

    return {
      readings: extractFlatReadings(sensors),
      sensors,
      sigfoxId,
      from,
      to,
      limit,
      cacheSource: 'api',
      cacheHitRatio: 0,
      latencyMs: Math.round(performance.now() - startTime)
    }
  }

  // Check if cache is enabled
  if (!cacheSettingsService.isCacheEnabled()) {
    const apiResult = await api.getReadings(sigfoxId, from, to, limit)
    const sensors = apiResult.data || {}

    return {
      readings: extractFlatReadings(sensors),
      sensors,
      sigfoxId,
      from,
      to,
      limit,
      cacheSource: 'api',
      cacheHitRatio: 0,
      latencyMs: Math.round(performance.now() - startTime)
    }
  }

  // CACHE-AUGMENTED STRATEGY:
  // 1. Check IndexedDB cache for known sensors and their readings
  // 2. ALWAYS call API to discover current sensors (cache may be missing new sensors)
  // 3. Merge cached readings with fresh API data
  // 4. Cache fresh data for future loads
  // NOTE: We never skip the API call even on "full cache hit" because the cache
  // only knows about sensors that existed when it was populated.

  const cachedSensorKeys = await readingsCacheService.getDeviceSensors(sigfoxId, email)

  let sensorKeys: string[] = []
  let reconstructedSensors: GetReadingsResponse = {}
  let overallCacheHitRatio = 0

  if (cachedSensorKeys.length > 0) {
    // Check cache for each sensor IN PARALLEL
    const cachePromises = cachedSensorKeys.map(async (sensorKey) => {
      const cacheKey = `${sigfoxId}-${sensorKey}`
      const cacheResult = await readingsCacheService.getCachedReadings(
        cacheKey,
        email,
        from,
        to
      )
      return { sensorKey, cacheResult }
    })

    const cacheResultsArray = await Promise.all(cachePromises)

    // Convert to Map for compatibility with existing code
    const cacheResults = new Map<string, {
      readings: Reading[]
      missingRanges: Array<{ from: number; to: number }>
      cacheHitRatio: number
      metadata?: any
    }>()

    for (const { sensorKey, cacheResult } of cacheResultsArray) {
      cacheResults.set(sensorKey, {
        readings: cacheResult.readings,
        missingRanges: cacheResult.missingRanges,
        cacheHitRatio: cacheResult.cacheHitRatio,
        metadata: cacheResult.metadata
      })
    }

    // Calculate overall cache hit ratio based on time coverage
    let totalRequested = 0
    let totalCached = 0
    for (const result of cacheResults.values()) {
      totalRequested += (to - from)
      totalCached += (to - from) * result.cacheHitRatio
    }
    overallCacheHitRatio = totalRequested > 0 ? totalCached / totalRequested : 0

    // ALWAYS extract cached data to reconstructedSensors, even for partial hits
    for (const [sensorKey, result] of cacheResults.entries()) {
      if (result.readings.length > 0) {
        // Convert array back to object format that charts expect
        const readingsObject: { [timestamp: string]: any } = {}
        result.readings.forEach(reading => {
          readingsObject[reading.timestamp.toString()] = reading.value
        })

        reconstructedSensors[sensorKey] = {
          metadata: result.metadata || {
            productName: result.metadata?.productName || '',
            sensorName: result.metadata?.sensorName || '',
            units: result.metadata?.units || ''
          },
          readings: readingsObject,
          // For backward compatibility, also include SensorData format
          productName: result.metadata?.productName || '',
          sensorName: result.metadata?.sensorName || '',
          units: result.metadata?.units || '',
          data: result.readings
        }
      }
    }

    // NOTE: We intentionally do NOT early-return on full cache hits.
    // The API must always be called to discover new sensors that may have been
    // added to the device since the cache was populated. The cache is used for
    // performance (pre-populating data for merge), not as source of truth for
    // what sensors exist. See: https://github.com/issues/missing-charts-bug
  }

  // Fetch from API (either first load or cache miss)
  const apiResult = await api.getReadings(sigfoxId, from, to, limit)
  const apiData = apiResult.data || apiResult || {}
  sensorKeys = Object.keys(apiData)

  if (sensorKeys.length === 0) {
    return {
      readings: [],
      sensors: {},
      sigfoxId,
      from,
      to,
      limit,
      cacheSource: 'api',
      cacheHitRatio: 0,
      latencyMs: Math.round(performance.now() - startTime)
    }
  }

  // Process API data: merge with cached data and cache fresh readings
  for (const [sensorKey, sensorData] of Object.entries(apiData) as [string, any][]) {

    if (!sensorData || typeof sensorData !== 'object') {
      console.warn(`⚠️ Sensor ${sensorKey} has invalid data structure, skipping`)
      continue
    }

    const cacheKey = `${sigfoxId}-${sensorKey}`

    // API returns: { metadata: {...}, readings: { timestamp: value } }
    // We need to convert to array: [{ timestamp: ts, value: val }, ...]
    let freshReadings: Reading[] = []

    // Check all possible data locations
    const hasReadings = sensorData.readings !== undefined
    const isDataArray = Array.isArray(sensorData.data)
    const isReadingsObject = hasReadings && typeof sensorData.readings === 'object' && !Array.isArray(sensorData.readings)
    const isReadingsArray = Array.isArray(sensorData.readings)

    if (isDataArray) {
      freshReadings = sensorData.data
    } else if (isReadingsObject) {
      freshReadings = Object.entries(sensorData.readings).map(([timestamp, value]) => ({
        timestamp: Number(timestamp),
        value: value as any
      }))
    } else if (isReadingsArray) {
      freshReadings = sensorData.readings
    } else if (Array.isArray(sensorData)) {
      freshReadings = sensorData
    }

    if (freshReadings.length > 0) {
      // Store metadata with the fresh readings
      const metadata = {
        productName: sensorData.metadata?.productName || sensorData.productName || '',
        sensorName: sensorData.metadata?.sensorName || sensorData.sensorName || sensorKey,
        units: sensorData.metadata?.units || sensorData.units || ''
      }

      // Cache only the fresh readings (don't re-cache what we already have)
      await readingsCacheService.cacheReadings(
        cacheKey,
        email,
        freshReadings,
        { from, to },
        metadata
      )
    }
  }

  // Build final response with merged data (cached + fresh)
  // Transform to include .data array for UI compatibility
  const finalSensors: GetReadingsResponse = {}

  // Start with reconstructed sensors (from cache)
  for (const [sensorKey, cachedSensor] of Object.entries(reconstructedSensors)) {
    finalSensors[sensorKey] = cachedSensor
  }

  // Merge with API data
  for (const [sensorKey, sensorData] of Object.entries(apiData) as [string, any][]) {
    if (sensorData && typeof sensorData === 'object') {
      // Convert readings to array if it's an object
      let freshReadings: Reading[] = []
      if (Array.isArray(sensorData.data)) {
        freshReadings = sensorData.data
      } else if (sensorData.readings && typeof sensorData.readings === 'object' && !Array.isArray(sensorData.readings)) {
        freshReadings = Object.entries(sensorData.readings).map(([timestamp, value]) => ({
          timestamp: Number(timestamp),
          value: value as any
        }))
      } else if (Array.isArray(sensorData.readings)) {
        freshReadings = sensorData.readings
      }

      // Get cached data for this sensor
      const cachedData = finalSensors[sensorKey]?.data || []

      // Merge cached + fresh (deduplicates by timestamp, prefers fresh)
      const mergedData = freshReadings.length > 0 && cachedData.length > 0
        ? readingsCacheService.mergeReadings(cachedData, freshReadings)
        : freshReadings.length > 0
        ? freshReadings
        : cachedData

      // Convert merged array back to object format for readings
      const readingsObject: { [timestamp: string]: any } = {}
      mergedData.forEach(reading => {
        readingsObject[reading.timestamp.toString()] = reading.value
      })

      finalSensors[sensorKey] = {
        ...sensorData,
        data: mergedData,  // Merged array for UI
        readings: readingsObject,  // Object format for charts
        productName: sensorData.metadata?.productName || sensorData.productName || cachedData[0]?.productName || '',
        sensorName: sensorData.metadata?.sensorName || sensorData.sensorName || cachedData[0]?.sensorName || sensorKey,
        units: sensorData.metadata?.units || sensorData.units || cachedData[0]?.units || ''
      }
    }
  }

  const latencyMs = Math.round(performance.now() - startTime)

  return {
    readings: extractFlatReadings(finalSensors),
    sensors: finalSensors,
    sigfoxId,
    from,
    to,
    limit,
    cacheSource: overallCacheHitRatio > 0 ? 'mixed' : 'api',
    cacheHitRatio: overallCacheHitRatio,
    latencyMs
  }
}

/**
 * Fold the windows that have arrived so far into one response of the same shape
 * a single-range fetch produced. Per sensor the timestamp maps are unioned and
 * the `data` arrays concatenated, de-duplicated by timestamp and re-sorted —
 * windows tile the range without overlap, so collisions only happen on a
 * boundary second.
 */
function mergeReadingsWindows(
  parts: TransformedReadingsResponse[],
  sigfoxId: string,
  from: number,
  to: number,
  limit: number
): TransformedReadingsResponse {
  const sensors: GetReadingsResponse = {}

  for (const part of parts) {
    for (const [sensorKey, sensor] of Object.entries(part.sensors || {}) as [string, any][]) {
      if (!sensor || typeof sensor !== 'object') continue

      const existing = sensors[sensorKey] as any
      if (!existing) {
        sensors[sensorKey] = {
          ...sensor,
          readings: { ...(sensor.readings || {}) },
          hidden: { ...(sensor.hidden || {}) },
          data: [...(sensor.data || [])],
        }
        continue
      }

      Object.assign(existing.readings, sensor.readings || {})
      Object.assign(existing.hidden, sensor.hidden || {})

      const byTimestamp = new Map<number, Reading>()
      for (const reading of existing.data as Reading[]) byTimestamp.set(reading.timestamp, reading)
      for (const reading of (sensor.data || []) as Reading[]) byTimestamp.set(reading.timestamp, reading)
      existing.data = Array.from(byTimestamp.values()).sort((a, b) => a.timestamp - b.timestamp)
    }
  }

  const cacheHitRatio = parts.length > 0 ? parts.reduce((sum, p) => sum + (p.cacheHitRatio || 0), 0) / parts.length : 0

  return {
    readings: extractFlatReadings(sensors),
    sensors,
    sigfoxId,
    from,
    to,
    limit,
    cacheSource: parts.some((p) => p.cacheSource === 'api') ? (cacheHitRatio > 0 ? 'mixed' : 'api') : 'cache',
    cacheHitRatio,
    latencyMs: parts.reduce((max, p) => Math.max(max, p.latencyMs || 0), 0),
  }
}

/**
 * React Query hook for fetching device sensor readings.
 *
 * The requested range is split into render-sized windows (see
 * `splitIntoProgressiveWindows`) and each window is its own query, so:
 * - charts can draw as soon as the first (most recent) window lands instead of
 *   waiting out a whole quarter's worth of readings, and
 * - stepping the date range re-uses the windows already in cache.
 *
 * `isLoading` therefore means "nothing to draw yet"; `isFetching` stays true
 * while later windows are still in flight, which is what the page shows its
 * keep-loading indicator from.
 *
 * @param sigfoxId - Device sigfoxId to fetch readings for
 * @param from - Start timestamp (Unix seconds)
 * @param to - End timestamp (Unix seconds)
 * @param limit - Maximum number of readings to return per window (default: DEFAULT_LIMIT)
 * @param enabled - Whether to enable automatic fetching (default: true when sigfoxId exists)
 */
export function useDeviceReadings(
  sigfoxId: string,
  from: number,
  to: number,
  limit: number = DEFAULT_LIMIT,
  enabled?: boolean
) {
  const { email: contextEmail } = useAuthContext()
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId && from > 0 && to > 0

  const windows = useMemo(
    () => (sigfoxId && to > from ? splitIntoProgressiveWindows(from, to) : []),
    [sigfoxId, from, to]
  )

  const results = useQueries({
    queries: windows.map((window) => ({
      queryKey: queryKeys.readings(sigfoxId, window.from, window.to),
      queryFn: () => fetchReadingsRange(sigfoxId, window.from, window.to, limit, contextEmail),

      staleTime: 2 * 60 * 1000, // 2 minutes - balance between freshness and API calls
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in memory
      enabled: shouldFetch,
      retry: 2,
      retryDelay: 1000,

      // Enable background refetch when data becomes stale
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    })),
  })

  const loadedWindows = results.filter((r) => r.data !== undefined).length
  const totalWindows = windows.length
  const isFetching = results.some((r) => r.isFetching)
  const error = results.find((r) => r.error)?.error ?? null

  // Re-merge only when a window actually resolves. Merging on every render would
  // hand the charts a new object identity each time and repaint every canvas.
  const dataStamp = results.map((r) => r.dataUpdatedAt).join('|')
  const data = useMemo(
    () => {
      const parts = results.map((r) => r.data).filter(Boolean) as TransformedReadingsResponse[]
      if (parts.length === 0) return undefined
      return mergeReadingsWindows(parts, sigfoxId, from, to, limit)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataStamp, sigfoxId, from, to, limit]
  )

  return {
    data,
    error,
    isLoading: loadedWindows === 0 && isFetching,
    isFetching,
    /** True while some windows have rendered and others are still loading. */
    isPartial: loadedWindows > 0 && loadedWindows < totalWindows,
    loadedWindows,
    totalWindows,
  }
}


/**
 * Extract flat readings array from nested sensor data
 * For backward compatibility with components expecting flat array
 */
function extractFlatReadings(sensors: GetReadingsResponse | any): Reading[] {
  const allReadings: Reading[] = []

  if (!sensors || typeof sensors !== 'object') {
    return []
  }

  for (const [sensorKey, sensorData] of Object.entries(sensors) as [string, any][]) {
    if (!sensorData || typeof sensorData !== 'object') {
      continue
    }

    // Try different possible data structures
    let dataArray: Reading[] = []

    const hasReadings = sensorData.readings !== undefined
    const isDataArray = Array.isArray(sensorData.data)
    const isReadingsObject = hasReadings && typeof sensorData.readings === 'object' && !Array.isArray(sensorData.readings)
    const isReadingsArray = Array.isArray(sensorData.readings)
    const isSensorDataArray = Array.isArray(sensorData)

    if (isDataArray) {
      dataArray = sensorData.data
    } else if (isReadingsObject) {
      // Convert readings object to array
      dataArray = Object.entries(sensorData.readings).map(([timestamp, value]) => ({
        timestamp: Number(timestamp),
        value: value as any
      }))
    } else if (isReadingsArray) {
      dataArray = sensorData.readings
    } else if (isSensorDataArray) {
      dataArray = sensorData
    }

    if (Array.isArray(dataArray) && dataArray.length > 0) {
      // Add sensor key to each reading for identification
      const readingsWithSensor = dataArray.map(reading => ({
        ...reading,
        _sensorKey: sensorKey,
        _sensorName: sensorData.sensorName || sensorData.metadata?.sensorName || sensorKey,
        _units: sensorData.units || sensorData.metadata?.units?.symbol || ''
      }))
      allReadings.push(...readingsWithSensor)
    }
  }

  // Sort by timestamp
  return allReadings.sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
}

/**
 * React Query hook for fetching battery readings
 *
 * Battery data changes less frequently than sensor readings
 *
 * @param sigfoxId - Device sigfoxId to fetch readings for
 * @param from - Start timestamp (Unix seconds)
 * @param to - End timestamp (Unix seconds)
 * @param limit - Maximum number of readings to return (default: 5000)
 * @param enabled - Whether to enable automatic fetching
 */
export function useBatteryReadings(
  sigfoxId: string,
  from: number,
  to: number,
  limit: number = 5000,
  enabled?: boolean
) {
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId && from > 0 && to > 0

  return useQuery({
    queryKey: queryKeys.batteryReadings(sigfoxId, from, to),

    // STALE-WHILE-REVALIDATE: Show previous data while fetching
    // Only show previous data if it's for the same device (check via query key)
    placeholderData: (previousData, previousQuery) => {
      // Check if the previous query is for the same device
      const prevSigfoxId = previousQuery?.queryKey?.[1]
      if (previousData && prevSigfoxId === sigfoxId) {
        return previousData
      }
      return undefined
    },

    queryFn: async () => {
      const result = await api.getBatteryReadings(sigfoxId, from, to, limit)
      return result
    },

    // Battery data changes slowly, so longer stale time is acceptable
    staleTime: 5 * 60 * 1000, // 5 minutes - battery changes slowly
    gcTime: 15 * 60 * 1000, // 15 minutes
    enabled: shouldFetch,
    retry: 2,

    // Enable background refetch when stale
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

/**
 * React Query hook for fetching last reading for a device
 *
 * Used for displaying current sensor values
 *
 * @param sigfoxId - Device sigfoxId to fetch last reading for
 * @param enabled - Whether to enable automatic fetching
 */
export function useLastReading(sigfoxId: string, enabled?: boolean) {
  const shouldFetch = enabled !== undefined ? enabled : !!sigfoxId

  return useQuery({
    queryKey: queryKeys.lastReadings(sigfoxId),

    // STALE-WHILE-REVALIDATE: Show previous reading while fetching latest
    // Only show previous data if it's for the same device
    placeholderData: (previousData, previousQuery) => {
      const prevSigfoxId = previousQuery?.queryKey?.[1]
      if (previousData && prevSigfoxId === sigfoxId) {
        return previousData
      }
      return undefined
    },

    queryFn: async () => {
      const result = await api.getLastReadings(sigfoxId)
      return result
    },

    staleTime: 30 * 1000, // 30 seconds - current value is time-sensitive
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 60 * 1000, // Auto-refresh every minute
    enabled: shouldFetch,
    retry: 2,

    // Enable background refetch
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}
